/**
 * Structured mailing-address fields (postal-updates.md) — line1 / line2 (optional) / city / state /
 * zip, reused on SubscribePage (delivery=mail/both) and PreferencesPage. Each field is a `Field`
 * block (label → control → helper/error, WCAG 3.3.1) so error text is announced and `aria-invalid`
 * is set. `idPrefix` lets each caller mint unique ids (SubscribePage vs. PreferencesPage on the
 * same route tree, and the error summary needs a stable id to focus).
 */
import { Field } from "./Field.js";
import type { MailingAddress } from "../api/types.js";

export interface MailingAddressErrors {
  line1?: string;
  city?: string;
  state?: string;
  zip?: string;
}

interface Props {
  idPrefix: string;
  value: MailingAddress;
  onChange: (next: MailingAddress) => void;
  errors?: MailingAddressErrors;
  /** Legend text for the fieldset — defaults to "Mailing address". */
  legend?: string;
}

export function mailingAddressFieldIds(idPrefix: string): {
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
  legend: string;
} {
  return {
    line1: `field-${idPrefix}-line1`,
    line2: `field-${idPrefix}-line2`,
    city: `field-${idPrefix}-city`,
    state: `field-${idPrefix}-state`,
    zip: `field-${idPrefix}-zip`,
    legend: `field-${idPrefix}-legend`,
  };
}

/** Client-side mirror of the backend's validation (postal-updates.md "Validation (shared)"): line1/
 *  city/state/zip all required; state 2-letter, zip 5 or 9-digit. Returns an empty object when valid. */
export function validateMailingAddress(addr: MailingAddress): MailingAddressErrors {
  const errors: MailingAddressErrors = {};
  if (!addr.line1.trim()) errors.line1 = "Enter a street address.";
  if (!addr.city.trim()) errors.city = "Enter a city.";
  const state = addr.state.trim().toUpperCase();
  if (!state) errors.state = "Enter a state.";
  else if (!/^[A-Z]{2}$/.test(state)) errors.state = "Use the 2-letter state abbreviation, like KS.";
  const zip = addr.zip.trim();
  if (!zip) errors.zip = "Enter a ZIP code.";
  else if (!/^\d{5}(-?\d{4})?$/.test(zip)) errors.zip = "Enter a 5-digit ZIP, like 66040 (or ZIP+4).";
  return errors;
}

export function MailingAddressFields({
  idPrefix,
  value,
  onChange,
  errors,
  legend = "Mailing address",
}: Props): JSX.Element {
  const ids = mailingAddressFieldIds(idPrefix);
  const set = (patch: Partial<MailingAddress>) => onChange({ ...value, ...patch });

  return (
    <fieldset className="td-fieldset">
      <legend className="td-fieldset__legend" id={ids.legend}>
        {legend}
      </legend>

      <Field id={ids.line1} label="Street address" required error={errors?.line1}>
        {(aria) => (
          <input
            {...aria}
            className="td-input"
            type="text"
            autoComplete="address-line1"
            value={value.line1}
            onChange={(e) => set({ line1: e.target.value })}
          />
        )}
      </Field>

      <Field id={ids.line2} label="Apt / unit / suite" optionalHint>
        {(aria) => (
          <input
            {...aria}
            className="td-input"
            type="text"
            autoComplete="address-line2"
            value={value.line2 ?? ""}
            onChange={(e) => set({ line2: e.target.value })}
          />
        )}
      </Field>

      <div className="td-field-row">
        <Field id={ids.city} label="City" required error={errors?.city}>
          {(aria) => (
            <input
              {...aria}
              className="td-input"
              type="text"
              autoComplete="address-level2"
              value={value.city}
              onChange={(e) => set({ city: e.target.value })}
            />
          )}
        </Field>

        <Field id={ids.state} label="State" required error={errors?.state}>
          {(aria) => (
            <input
              {...aria}
              className="td-input td-input--short"
              type="text"
              inputMode="text"
              autoComplete="address-level1"
              maxLength={2}
              value={value.state}
              onChange={(e) => set({ state: e.target.value.toUpperCase() })}
            />
          )}
        </Field>

        <Field id={ids.zip} label="ZIP code" required error={errors?.zip}>
          {(aria) => (
            <input
              {...aria}
              className="td-input td-input--short"
              type="text"
              inputMode="numeric"
              autoComplete="postal-code"
              maxLength={10}
              value={value.zip}
              onChange={(e) => set({ zip: e.target.value })}
            />
          )}
        </Field>
      </div>
    </fieldset>
  );
}

export function emptyMailingAddress(): MailingAddress {
  return { line1: "", line2: "", city: "", state: "", zip: "" };
}
